import React, { useState, useEffect } from 'react';
import Form from 'global/components/form';
import { get, set, orderBy, first, size } from '../../../global/lib/lodash';
import useStyles from './styles';
import { Column, Row } from '../../../global/components/row';
import { Text } from 'global/components/text';
import useRetrieveData from 'global/hooks/useRetrieveData';
import { Checkbox, Icon, IconButton } from '@material-ui/core';
import { Button } from '../../../global/components/Button';
import { SortableList } from 'global/components/sortable-list';
import FormCheckpoint from '../../../global/components/form-checkpoint';
import { getDisplayScore } from 'global/utils/getDisplayScore';
import ScoreIcon from 'global/components/ScoreIcon';
import { _size, convertToPostStructure } from 'global/utils';
import sendData from 'redux/actions/sendData';
import SnackAlerts from '../../../global/components/snack-alerts';
import Modal from 'global/components/modal';
import { guid, isNewItem, scoringClass } from '../../../global/utils';
import { useScreen } from '../../../global/styles';

const YesNo = [
  { value: 1, title: 'Yes' },
  { value: -1, title: `No` },
];

//Because MUI is not good at displaying empty values in ui
const fixMinus = (obj) => {
  const newobj = {};
  for (let k in obj) {
    const val = obj[k];
    if (val === -1) {
      newobj[k] = 0;
    } else if (val === 'null') {
      newobj[k] = null;
    } else if (val === '0') {
      newobj[k] = 0;
    } else {
      newobj[k] = val;
    }
  }
  return newobj;
};

export const CreateScorecard = (props) => {
  const classes = useStyles();
  const [alerts, setAlerts] = useState();
  const [fs, setfs] = useState({});
  const [state, setState] = useState({
    loading: false,
  });

  const scorecards = useRetrieveData(
    { url: '/scorecards' },
    {
      build: ['scorecards'],
      useCache: { reload: 20 },
      params: {
        include: 'checklists,checklists.classification,checklists.classification.system',
      },
    }
  );

  const formatData = async () => {
    const obj = {
      type: 'scorecards',
    };

    const hasSelectedScorecard = fs.scorecard ? get(scorecards, 'data', []).find((s) => s.id == fs.scorecard) : undefined;

    const config = hasSelectedScorecard ? get(hasSelectedScorecard, 'config') : fs.config;

    const sysWeights = get(fs, 'weights', []);

    const orderOverrides = [];
    const weightOverrides = [];

    const fullSystems = await Promise.all(
      sysWeights.map(async (w) => {
        const { system } = w;
        if (isNewItem({ id: w.key })) {
          const result = await sendData(
            {
              url: '/supersets',
              method: 'post',
              data: {
                data: {
                  type: 'supersets',
                  attributes: {
                    name: system.name,
                  },
                },
              },
            },
            { normalize: { build: ['supersets'] } }
          );
          return {
            ...w,
            system: {
              ...system,
              ...first(result),
            },
          };
        } else {
          return w;
        }
      })
    );

    fullSystems.map((w, i) => {
      const { system, items } = w;
      orderOverrides.push({ type: 'Superset', value: i, id: system.id });

      if (system.weight) {
        weightOverrides.push({ type: 'Superset', value: system.weight, id: system.id });
      }

      items.map((cl, j) => {
        orderOverrides.push({ type: 'Checklist', value: j, id: cl.id });

        if (cl.weight) {
          weightOverrides.push({ type: 'Checklist', value: cl.weight, id: cl.id });
        }
      });
    });

    set(config, 'orderOverrides', orderOverrides);
    set(config, 'weightOverrides', weightOverrides);

    set(
      obj,
      'attributes',
      fixMinus({
        name: get(fs, 'name', ''),
        edition_id: get(props, 'editionId'),
        realm_id: get(props, 'doc.realm.id'),
        config: convertToPostStructure(fixMinus(config)),
        active: fs.active,
        'created-at': new Date().toISOString(),
        'updated-at': new Date().toISOString(),
      })
    );

    set(obj, 'relationships.checklists', {
      data: Object.keys(fs.checklists)
        .filter((cl_k) => fs.checklists[cl_k])
        .map((cl) => ({ type: 'checklists', id: cl })),
    });

    return {
      data: obj,
    };
  };

  const onSubmit = async () => {
    try {
      setState((s) => ({ ...s, loading: true }));

      const data = await formatData();

      const resp = await sendData({
        url: `/scorecards`,
        method: 'post',
        data,
      });

      props.onClose && props.onClose(true);
    } catch (err) {
      setAlerts({ message: err.message, severity: 'error' });
    } finally {
      setState((s) => ({ ...s, loading: false }));
    }
  };

  const isCustom = get(fs, 'copy', 1) < 0;

  const edocs = get(props, 'doc.checklists', []).map((e) => ({ ...e, order: parseInt(get(e, 'classification.system.id')) }));
  const scorecard = !isCustom ? scorecards.data.find((sc) => sc.id == get(fs, 'scorecard')) : undefined;
  const docs = orderBy(edocs, 'order');

  return (
    <div className={classes.createSc}>
      <Form
        onFormState={(f) => setfs((ff) => ({ ...ff, ...f }))}
        showPosition
        onSubmit={onSubmit}
        loading={state.loading}
        screens={[
          {
            title: 'Step 1: General Information',
            positionTitle: 'Info',
            inputs: [
              {
                id: 'name',
                type: 'TextField',
                defaultValue: () => get(props, 'doc.name', ''),
                required: true,
                props: {
                  label: 'Name',
                },
              },
              {
                id: 'active',
                type: 'Select',
                defaultValue: () => 1,
                data: [
                  { value: 1, title: 'True' },
                  { value: -1, title: 'False' },
                ],
                props: {
                  label: 'Active',
                },
              },
              {
                id: 'copy',
                type: 'Select',
                defaultValue: () => 1,
                data: [
                  { value: 1, title: 'Yes' },
                  { value: -1, title: `No, I'll set it up myself` },
                ],
                props: {
                  label: 'Would you like to copy info from a previous scorecard?',
                },
              },
              isCustom
                ? {
                    id: 'config',
                    component: () => <CustomComponent {...fs} onFormState={(f) => setfs((fs) => ({ ...fs, config: f }))} />,
                  }
                : {
                    id: 'scorecard',
                    type: 'Select',
                    data: scorecards.data,
                    required: get(fs, 'copy') === 1,
                    item: (item, i) => {
                      return { value: item.id, title: item.name };
                    },
                    props: {
                      label: 'Scorecard',
                    },
                  },
            ],
          },
          {
            title: 'Step 2: Choose Checklists',
            positionTitle: 'Checklists',
            inputs: [
              {
                id: 'checklists',
                component: () => <EdDocList {...fs} {...props} scorecard={scorecard} docs={docs} onFormState={(f) => setfs((fs) => ({ ...fs, checklists: f }))} />,
              },
            ],
          },
          {
            title: 'Step 3: Order and Weights',
            positionTitle: 'Details',
            inputs: [
              {
                id: 'weights',
                component: () => <Weights {...fs} {...props} scorecard={scorecard} docs={docs} onFormState={(f) => setfs((fs) => ({ ...fs, weights: f }))} />,
              },
            ],
          },
        ]}
      />
      <SnackAlerts alerts={alerts} setAlerts={setAlerts} />
    </div>
  );
};

const CustomComponent = (props) => {
  const [fs, setfs] = useState(props.config || {});
  const [scores, setScores] = useState({});

  const maxScore = parseInt(fs.maxScore || 1);
  const minScore = parseInt(fs.minScore || 0);
  const step = parseInt(fs.step || 1);
  const isSummed = scoringClass(fs).isSummed;

  const { isMobile } = useScreen();

  useEffect(() => {
    props.onFormState(fs);
  }, [fs]);

  const getScOptions = () => {
    const ret = [];

    for (let i = minScore; (i <= maxScore && i >= minScore) || (i >= maxScore && i <= minScore); i += step) {
      ret.push({
        scorecardId: 1,
        checklistId: 1,
        value: ((i - minScore) * 100) / (maxScore - minScore || 1),
        criteria: 'Text',
        weight: 1,
      });
    }

    return ret;
  };

  const defaultScoreOptions = () => {
    const ret = [];

    const { allowNA, allowNO } = fs || {};
    if (!!allowNA) {
      ret.push({ title: 'N/A', value: 'null' });
    }

    if (!!allowNO) {
      ret.push({ title: 'N/O', value: -1 });
    }

    getScOptions().map((s) => {
      ret.push({ title: s.value, value: `${s.value}` });
    });

    return ret;
  };

  const onScore = (d, key) => {
    const data = d.build();

    setScores((s) => ({ ...s, [key]: parseFloat(get(data, 'data.attributes.score')) }));
  };

  const score = (() => {
    // "Summed" method uses very different logic for calculations
    if (isSummed) {
      const { sum } = Object.keys(scores).reduce(
        (agg, item) => {
          if (!isNaN(scores[item]) && scores[item] !== -1) {
            agg.sum += (scores[item] / 100) * (maxScore - minScore) + minScore;
          }
          return agg;
        },
        { sum: 0 }
      );

      return ((sum - minScore) * 100) / (maxScore - minScore || 1);
    } else {
      const { sum, count } = Object.keys(scores).reduce(
        (agg, item) => {
          if (!isNaN(scores[item])) {
            if (scores[item] === -1) {
              if (get(agg, 'sum') === null) {
                agg.sum = -1;
              }
            } else {
              if (get(agg, 'sum') === -1 || isNaN(get(agg, 'sum'))) {
                agg.sum = 0;
              }
              agg.sum += scores[item];
              agg.count += 1;
            }
          }
          return agg;
        },
        { sum: null, count: 0 }
      );

      if (sum >= 0) {
        return sum / count;
      } else {
        return sum;
      }
    }
  })();

  const _fs = fixMinus(fs);

  const formProps = {
    checkpoint: {
      data: {
        name: 'Title',
        description: 'Checkpoint description and some additional text to make it look right',
        scorecardOptions: getScOptions(),
      },
    },
    assessmentId: '1',
    checklist: { checklistId: 1 },
    checkpoint: { scorecardId: 1 },
    scorecard: { config: { ..._fs, maxScore, minScore, step } },
    assessment: {},
    hideRightGrid: true,
  };

  const isPassFail = _size(get(formProps, 'checkpoint.data.scorecardOptions')) == 2;

  const CONT = isMobile ? Column : Row;

  return (
    <CONT dontCenter>
      <div style={{ width: isMobile ? '100%' : '47.5%' }}>
        <Text bold>Custom Configuration</Text>
        <Form
          onFormState={setfs}
          showAllScreens
          screens={[
            {
              // allowNA: 1
              // allowNO: 1
              // defaultScore: null
              // maxScore: 1
              // minScore: 0
              // scoringClass: "App\\Calculators\\ScorecardAggregated"
              // step: 1
              inputs: [
                {
                  id: 'allowNA',
                  type: 'Select',
                  defaultValue: () => get(fs, 'allowNA', 1),
                  data: YesNo,
                  props: {
                    label: 'Allow N/A',
                  },
                },
                {
                  id: 'allowNO',
                  type: 'Select',
                  defaultValue: () => get(fs, 'allowNO', 1),
                  data: YesNo,
                  props: {
                    label: 'Allow N/O',
                  },
                },
                {
                  id: 'minScore',
                  type: 'TextField',
                  defaultValue: () => get(fs, 'minScore', 0),
                  props: {
                    label: 'Min Score',
                    type: 'number',
                  },
                },
                {
                  id: 'step',
                  type: 'TextField',
                  defaultValue: () => get(fs, 'step', 1),
                  props: {
                    label: 'Step',
                    type: 'number',
                  },
                },
                {
                  id: 'maxScore',
                  type: 'TextField',
                  defaultValue: () => get(fs, 'maxScore', 1),
                  props: {
                    label: 'Max Score',
                    type: 'number',
                  },
                },
                {
                  id: 'defaultScore',
                  type: 'Select',
                  defaultValue: () => get(fs, 'defaultScore', 'null'),
                  data: defaultScoreOptions,
                  props: {
                    label: 'Default Score',
                    displayEmpty: true,
                  },
                },
                {
                  id: 'scoringClass',
                  type: 'Select',
                  defaultValue: () => get(fs, 'scoringClass', 'aggregated'),
                  data: [
                    { value: 'aggregated', title: 'Average' },
                    // Labeled "Experimental" until further notice
                    { value: 'summed', title: 'Sum (Experimental)' },
                  ],
                  props: {
                    label: 'Calculator',
                  },
                },
              ],
            },
          ]}
        />
      </div>

      {!isMobile && <div style={{ width: '5%' }} />}

      <div style={{ width: isMobile ? '100%' : '45%' }}>
        <Text bold>Preview</Text>
        <Row>
          <span>{getDisplayScore({ value: score }, 0, { ..._fs, maxScore, minScore, step, isPassFail })}</span>
          <ScoreIcon score={isSummed ? ((((score / 100) * (maxScore - minScore) + minScore) / 2 - minScore) * 100) / (maxScore - minScore || 1) : score} />
          <Text>Checklist</Text>
        </Row>
        <div style={{ margin: isMobile ? 8 : undefined }}>
          <FormCheckpoint {...formProps} addToQueue={(d) => onScore(d, '1')} />
          <FormCheckpoint {...formProps} addToQueue={(d) => onScore(d, '2')} />
        </div>
      </div>
    </CONT>
  );
};

const EdDocList = (props) => {
  const docs = props.docs;

  const [sel, setsel] = useState(get(props, 'checklists', {}));

  const widths = [
    { width: '7%', style: { minWidth: 42 }, title: '' },
    { width: '73%', title: 'Title', sel: 'name' },
    { width: '20%', title: 'System', sel: 'classification.system.name' },
  ];

  useEffect(() => {
    props.onFormState(sel);
  }, [sel]);

  useEffect(() => {
    if (!_size(sel)) {
      const ss = {};
      const editionDocChecks = get(props, 'doc.editionDocuments', [])
        .filter((f) => get(f, 'documentableType') == 'checklist')
        .map((d) => `${d.documentableId}`);
      const scchecks = get(props, 'scorecard.checklists', []);

      docs.map((d) => {
        ss[d.id] = !!scchecks.find((s) => s.id == d.id) && !!editionDocChecks.includes(d.id);
      });

      setsel(ss);
    }
  }, []);

  return (
    <div>
      <Row fullWidth style={{ borderBottom: '1px solid black' }}>
        {widths.map((w) => (
          <div key={w.title} style={{ width: w.width, ...get(w, 'style', {}) }}>
            <Text>{w.title}</Text>
          </div>
        ))}
      </Row>

      {docs.map((edoc) => {
        const issel = !!get(sel, edoc.id);
        return (
          <Row fullWidth>
            {widths.map((w) => {
              return (
                <div style={{ width: w.width, ...get(w, 'style', {}) }}>
                  {!w.title ? <Checkbox checked={issel} onChange={() => setsel((s) => ({ ...s, [edoc.id]: !issel }))} /> : <Text>{get(edoc, w.sel)}</Text>}
                </div>
              );
            })}
          </Row>
        );
      })}
    </div>
  );
};

const Weights = (props) => {
  const [cls, setcls] = useState([]);
  const [state, setState] = useState({ force: 0 });
  const [editModal, setEditModal] = useState({
    visible: false,
    onClose: () => {
      setEditModal((s) => ({ ...s, visible: false }));
    },
  });

  const widths = [
    { width: '9%', title: '' },
    { width: '91%', title: 'Title', sel: 'name' },
  ];

  const autoOrder = () => {
    alert('TODO');
  };

  const onSort = (order) => {
    setcls(order);
  };

  const sortItem = (order, item) => {
    item.items = order;
    setState((s) => ({ ...s, force: s.force + 1 }));
  };

  const renderSubItem = (item, fullItem) => {
    const hasWeight =
      item.weight ||
      get(
        get(props, 'scorecard.config.weightOverrides', []).find((f) => f.id == item.id && f.type == 'Checklist'),
        'value'
      );

    return (
      <div style={{ paddingTop: 15 }}>
        <Row>
          <IconButton onClick={() => editItem(item, fullItem)} size="small">
            <Icon>edit</Icon>
          </IconButton>
          {item.name}
        </Row>
        {!!hasWeight && <Text type="label_small">{`Weight: ${hasWeight}`}</Text>}
      </div>
    );
  };

  const renderItem = (item) => {
    const { system, items } = item;

    const hasWeight =
      system.weight ||
      get(
        get(props, 'scorecard.config.weightOverrides', []).find((f) => f.id == system.id && f.type == 'Superset'),
        'value'
      );

    return (
      <div style={{ position: 'relative', paddingTop: 15 }}>
        <Row>
          <IconButton onClick={() => editSuperset(system)} size="small">
            <Icon>edit</Icon>
          </IconButton>
          {system.name}
          {!size(items) && (
            <IconButton style={{ marginLeft: 'auto' }} onClick={() => deleteSuperset(item)} size="small">
              <Icon>delete</Icon>
            </IconButton>
          )}
        </Row>
        {!!hasWeight && <Text type="label_small">{`Weight: ${hasWeight}`}</Text>}

        <SortableList verticalOnly items={items.map((it) => ({ ...it, key: it.id, content: renderSubItem(it, item) }))} onSort={(o) => sortItem(o, item, cls)} />

        <div style={{ width: 'calc(100% + 20px)', marginLeft: -20, marginTop: 10, borderBottom: '1px solid black' }} />
      </div>
    );
  };

  const closeModal = () => {
    setEditModal((s) => ({
      ...s,
      visible: false,
      superset: undefined,
      item: undefined,
      isNew: false,
      onClose: () => {
        setEditModal((s) => ({ ...s, visible: false }));
      },
    }));
  };

  const addNew = () => {
    setEditModal((s) => ({
      ...s,
      title: 'Add New System',
      visible: !s.visible,
      isNew: true,
      onClose: (fs) => {
        if (!!fs) {
          const obj = {
            system: fs,
            items: [],
            key: `new_${guid()}`,
          };
          setcls((c) => [...c, { ...obj, content: renderItem(obj) }]);
        }

        closeModal();
      },
    }));
  };

  const editSuperset = (superset) => {
    setEditModal((s) => ({
      ...s,
      title: 'Edit System',
      visible: !s.visible,
      superset,
      onClose: (fs) => {
        if (!!fs) {
          Object.keys(fs).map((k) => {
            set(superset, k, fs[k]);
          });
          setState((s) => ({ ...s, force: s.force + 1 }));
        }

        closeModal();
      },
    }));
  };

  const deleteSuperset = (superset) => {
    setcls((cl) => cl.filter((c) => c.key !== superset.key));
  };

  const editItem = (item, fullItem) => {
    setEditModal((s) => ({
      ...s,
      title: 'Edit Checklist',
      visible: !s.visible,
      item,
      onClose: (fs, editProps) => {
        if (!!fs) {
          Object.keys(fs).map((k) => {
            if (k !== 'moveTo') {
              set(item, k, fs[k]);
            }
          });

          if (fs.moveTo) {
            fullItem.items = fullItem.items.filter((f) => f.id !== item.id);
            const newcls = [...get(editProps, 'list', [])].map((c) => {
              if (c.key == fs.moveTo) {
                c.items = [...c.items, item];
              }
              return c;
            });
            setcls(newcls);
          }

          setState((s) => ({ ...s, force: s.force + 1 }));
        }

        closeModal();
      },
    }));
  };

  useEffect(() => {
    props.onFormState && props.onFormState(cls);
  }, [cls, state]);

  useEffect(() => {
    const sel = get(props, 'checklists', {});

    const validchecks = props.docs.filter((d) => !!sel[d.id]);

    const systems = {};

    validchecks.map((check) => {
      const system = get(check, 'classification.system');
      set(systems, system.id, {
        system,
        items: [...get(systems, [system.id, 'items'], []), check],
      });
    });

    const arr = orderBy(
      Object.keys(systems).map((k, i) => {
        const { system, items } = systems[k];

        const order = get(props, 'scorecard.config.orderOverrides', []).find((f) => f.id == system.id && f.type == 'Superset') || i;

        const obj = {
          system,
          items: orderBy(
            items.map((item, _i) => {
              const itemorder = get(props, 'scorecard.config.orderOverrides', []).find((f) => f.id == item.id && f.type == 'Checklist') || _i;
              return {
                ...item,
                order: itemorder,
              };
            }),
            'order'
          ),
          order,
          key: system.id,
        };

        obj.content = () => renderItem(obj);

        return obj;
      }),
      'order'
    );

    setcls(arr);
  }, []);

  return (
    <div>
      <Row>
        <Text bold>Checklists</Text>
        <Button icon={<Icon>refresh</Icon>} variant={'default'} title="Auto Order" onClick={autoOrder} />
      </Row>

      <Row fullWidth style={{ borderBottom: '1px solid black' }}>
        {widths.map((w) => (
          <div key={w.title} style={{ width: w.width }}>
            <Text>{w.title}</Text>
          </div>
        ))}
      </Row>

      <SortableList items={cls.map((c) => ({ ...c, content: renderItem(c) }))} verticalOnly onSort={onSort} />

      <div style={{ marginTop: 8 }}>
        <Button title="Add New Section" onClick={addNew} icon={<Icon>add</Icon>} variant="outlined" />
      </div>

      <EditModal {...editModal} list={cls} />
    </div>
  );
};

const EditModal = (props) => {
  const [formState, setFormState] = useState({});

  const onClose = (fs) => {
    props.onClose(fs, props);
  };

  const visible = !!props.visible;

  const item = props.item || props.superset;

  const list = props.list || [];

  return (
    <Modal {...props} open={visible} onClose={() => onClose()}>
      {!!visible && (
        <Form
          showAllScreens
          onFormState={setFormState}
          screens={[
            {
              inputs: [
                {
                  id: 'name',
                  type: 'TextField',
                  defaultValue: () => get(item, 'name', ''),
                  props: {
                    label: 'Name',
                    disabled: !props.isNew,
                  },
                },
                {
                  id: 'weight',
                  type: 'TextField',
                  defaultValue: () => get(item, 'weight', ''),
                  props: {
                    label: 'Weight',
                    type: 'number',
                  },
                },
                props.item && {
                  id: 'moveTo',
                  type: 'Select',
                  data: list.map((l) => ({ title: get(l, 'system.name'), value: get(l, 'key') })),
                  props: {
                    label: 'Move to',
                  },
                },
              ].filter((f) => !!f),
            },
          ]}
        />
      )}
      <Button title="Save" onClick={() => onClose(formState)} />
    </Modal>
  );
};
